import { forwardRef, useState, type ChangeEvent, type FocusEvent, type InputHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Lock } from '@/assets/icons'
import StringTooltip from '@/components/ui/StringTooltip'
import { getWorkEmailValidationMessage } from './work-email-policy'

export interface WorkEmailInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'placeholder' | 'type'> {
  /** Stable id used to associate the label and supporting messages with the input. */
  id: string
  /** Visible field label. */
  label?: string
  /** Validation message supplied by the owning form. */
  error?: string
  /** Policy applied on blur; defaults to the complete work-email policy. */
  getPolicyError?: (email: string) => string | undefined
  /** Optional supporting text shown below the input. */
  helperText?: ReactNode
  /** Additional classes for the field wrapper. */
  containerClassName?: string
  /** Optional full-value tooltip, including copy support for disabled prefilled fields. */
  tooltipProps?: {
    title: string
    copyToClipboard?: boolean
  }
}

/**
 * Shared work-email field presentation.
 *
 * The component provides consistent work-email policy feedback on blur. The owning form
 * remains responsible for enforcing the policy on submission and for any server validation.
 */
export const WorkEmailInput = forwardRef<HTMLInputElement, WorkEmailInputProps>(function WorkEmailInput(
  {
    id,
    label = 'Work email address',
    error,
    getPolicyError = getWorkEmailValidationMessage,
    helperText,
    containerClassName,
    className,
    required,
    disabled,
    autoComplete = 'email',
    onBlur,
    onChange,
    tooltipProps,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    ...inputProps
  },
  ref
) {
  const [policyError, setPolicyError] = useState<string>()
  const displayedError = error ?? (disabled ? undefined : policyError)
  const helperTextId = helperText ? `${id}-helper` : undefined
  const errorId = displayedError ? `${id}-error` : undefined
  const describedBy = [ariaDescribedBy, helperTextId, errorId].filter(Boolean).join(' ') || undefined

  const updatePolicyError = (value: string) => {
    const nextPolicyError = disabled ? undefined : getPolicyError(value)
    setPolicyError(nextPolicyError)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    updatePolicyError(event.currentTarget.value)
    onBlur?.(event)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (policyError !== undefined) {
      updatePolicyError(event.currentTarget.value)
    }
    onChange?.(event)
  }

  const inputContainer = (
    <div className="float-label relative">
      <input
        {...inputProps}
        ref={ref}
        id={id}
        type="email"
        placeholder=" "
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleChange}
        aria-invalid={displayedError ? true : ariaInvalid}
        aria-describedby={describedBy}
        className={clsx(
          'w-full rounded border-0 bg-surface-field ring-1 ring-inset focus:ring-inset focus:outline-hidden',
          'disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:text-brand-neutral-700',
          displayedError ? 'ring-border-field-error focus:ring-border-field-error' : 'ring-border-field focus:ring-border-field-active',
          className
        )}
      />
      <label htmlFor={id} className="text-text-tertiary">
        {label}
        {required && (
          <span className="ml-[2px] text-red-700" aria-hidden>
            *
          </span>
        )}
      </label>

      {disabled && (
        <>
          <div
            className="pointer-events-none absolute inset-0 rounded ring-1 ring-inset ring-border-strong"
            style={{ background: 'linear-gradient(to right, transparent 0%, transparent 70%, var(--surface-subtle) 90%)' }}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <Lock className="h-6 w-6 cursor-not-allowed text-text-tertiary" />
          </div>
        </>
      )}
    </div>
  )

  return (
    <div className={clsx('flex w-full flex-col gap-1', containerClassName)}>
      {tooltipProps?.title ? (
        <StringTooltip title={tooltipProps.title} copyToClipboard={tooltipProps.copyToClipboard} placement="bottom-start">
          {inputContainer}
        </StringTooltip>
      ) : (
        inputContainer
      )}

      {helperText && (
        <p id={helperTextId} className="mt-1 rounded bg-surface-subtle px-3 py-2 text-xs text-text-tertiary">
          {helperText}
        </p>
      )}

      {displayedError && (
        <div id={errorId}>
          <InlineMessage variant="error">{displayedError}</InlineMessage>
        </div>
      )}
    </div>
  )
})
